import React, { useEffect } from 'react';
import { Flex, FormInput } from '@sparrowengg/twigs-react';
import { AppDetailsProps } from '../types';
import {
  fileUploadTypes,
  embedUrlRegex,
  youtubeRegex,
  appTypeConstants
} from '../constants';
import { urlRegex } from 'commons/constants';

const AppDetails = ({
  app,
  register,
  errors,
  watch,
  isDirty,
  setValue,
  getValues,
  setIsDisabled
}: AppDetailsProps) => {
  const zipFIle = watch('zipFile');
  const icon = watch('icon');

  useEffect(() => {
    const { SIZE } = fileUploadTypes;
    if (!app) {
      if (SIZE in icon && SIZE in zipFIle) {
        setIsDisabled({ update: false, publish: false });
      }
    }
  }, [icon, zipFIle]);

  useEffect(() => {
    if (app) {
      setIsDisabled({ publish: false, update: false });
    }
  }, [app]);

  useEffect(() => {
    if (app && !isDirty) {
      setIsDisabled({ publish: false, update: true });
    } else {
      setIsDisabled((prev) => ({ ...prev, update: false }));
    }
  }, [isDirty]);

  return (
    <Flex gap="$20" css={{ width: '100%' }}>
      <Flex flexDirection="column" gap="$6" css={{ flex: 1 }}>
        <FormInput
          label="App Label"
          requiredIndicator
          {...register('appLabel', {
            required: 'App Label is required',
            validate: (value: string) =>
              value.trim() !== '' || 'App Label cannot be empty'
          })}
          size="lg"
          maxLength={100}
          value={watch('appLabel')}
          showCount
          error={errors?.appLabel?.message}
          placeholder="Ex: Survey Sparrow Label"
        />
        <FormInput
          label="Short Description"
          requiredIndicator
          {...register('shortDescription', {
            required: 'Short Description is required'
          })}
          size="lg"
          maxLength={100}
          showCount
          value={watch('shortDescription')}
          error={errors?.shortDescription?.message}
        />
        <FormInput
          label="App Overview"
          requiredIndicator
          {...register('appOverView', {
            required: 'App Overview is required'
          })}
          as="textarea"
          size="lg"
          maxLength={1000}
          showCount
          value={watch('appOverView')}
          css={{ resize: 'none', height: '120px', minHeight: '120px' }}
          error={errors?.appOverView?.message}
        />
        <FormInput
          label="App Instructions"
          requiredIndicator
          {...register('appInstructions', {
            required: 'App Instrctions is required'
          })}
          as="textarea"
          size="lg"
          maxLength={2000}
          value={watch('appInstructions')}
          showCount
          css={{ resize: 'none', height: '120px', minHeight: '120px' }}
          error={errors.appInstructions?.message}
        />
      </Flex>
      <Flex flexDirection="column" gap="$6" css={{ flex: 1 }}>
        <FormInput
          label="Video URL"
          requiredIndicator
          {...register('videoUrl', {
            required: 'Video URL is required',
            validate: (value) => {
              if (!(embedUrlRegex.test(value) || youtubeRegex.test(value))) {
                return 'Please enter a valid YouTube URL/embed URL';
              }
              return true;
            }
          })}
          size="lg"
          maxLength={200}
          value={watch('videoUrl')}
          error={errors.videoUrl?.message}
          placeholder="Ex: https://www.youtube.com/watch?v=eXdQW-P8zHs"
        />
        <FormInput
          label="Support Email"
          requiredIndicator
          {...register('supportEmail', {
            required: 'Support Email is required',
            validate: {
              matchPattern: (email: string) =>
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
                  email
                ) || 'Email address must be a valid address'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors.supportEmail?.message}
          placeholder="Ex: support@surveysparrow.com"
        />
        <FormInput
          label="Support URL"
          {...register('supportUrl', {
            required: false,
            pattern: {
              value: urlRegex,
              message: 'Please enter a valid URL'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors?.supportUrl?.message}
          placeholder="Ex: https://support.surveysparrow.com"
        />
        <FormInput
          label="Website Link"
          {...register('websiteLink', {
            pattern: {
              value: urlRegex,
              message: 'Please enter a valid URL'
            }
          })}
          size="lg"
          maxLength={200}
          error={errors?.websiteLink?.message}
          placeholder="http://app.samplesparrow.com"
        />
      </Flex>
    </Flex>
  );
};

export default AppDetails;
